import React, { useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { EventType, PublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";

// Exported so api.ts can acquire a fresh (auto-refreshed) token on every
// request instead of relying on a single ID token cached at login time --
// see api.ts's request interceptor for why that mattered.
export const msalInstance = new PublicClientApplication(msalConfig);

// 2026-08-11: fixes "had to click Sign In twice" on time.amplifyts.com.
// Two things were missing here: (1) msal-browser v3+ requires an explicit
// .initialize() call before any other MSAL API is touched, and (2) MSAL
// never automatically marks the account that just completed a
// loginRedirect as the "active" account -- something has to do that
// itself once handleRedirectPromise() resolves. Without both, the very
// first render after Azure AD redirects back could happen before MSAL
// finished processing the response, so the app briefly rendered the Login
// screen again even though sign-in had actually succeeded -- it only
// "took" on a second click because that one landed after the state had
// caught up. Gating rendering of MsalProvider (and everything under it)
// until this is fully resolved removes that race entirely. This is the
// same boilerplate Microsoft's own msal-react samples use.
let initPromise: Promise<void> | null = null;
function initializeMsal(): Promise<void> {
    if (!initPromise) {
        initPromise = msalInstance.initialize().then(async () => {
            const setActiveIfNeeded = (accounts: AccountInfo[]) => {
                if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
                    msalInstance.setActiveAccount(accounts[0]);
                }
            };

            msalInstance.addEventCallback((event) => {
                if (
                    (event.eventType === EventType.LOGIN_SUCCESS ||
                        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
                    event.payload &&
                    (event.payload as { account?: AccountInfo }).account
                ) {
                    msalInstance.setActiveAccount((event.payload as { account: AccountInfo }).account);
                }
            });

            setActiveIfNeeded(msalInstance.getAllAccounts());
            await msalInstance.handleRedirectPromise();
            setActiveIfNeeded(msalInstance.getAllAccounts());
        });
    }
    return initPromise;
}

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        initializeMsal().then(() => setReady(true));
    }, []);

    // Brief blank screen while MSAL resolves the redirect response --
    // sub-second in practice, and far better than flashing the Login page
    // for a moment on every successful sign-in.
    if (!ready) {
        return null;
    }

    return (
        <MsalProvider instance={msalInstance}>
            {children}
        </MsalProvider>
    );
};